const ROLE_STYLES = {
  organization: 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-200',
  user: 'border-sky-300/60 bg-sky-50 text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
  address: 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-200',
  controller: 'border-violet-300/70 bg-violet-50 text-violet-900 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-100',
  counter: 'border-emerald-300/70 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100',
  leak_sensor: 'border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  sensor: 'border-orange-300/70 bg-orange-50 text-orange-900 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-100',
  device: 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-200',
};

const ROLE_CAPTION = {
  organization: 'УК',
  user: 'Клиент',
  address: 'Адрес',
  controller: 'Контроллер',
  counter: 'Счётчик',
  leak_sensor: 'Протечка',
  sensor: 'Датчик',
  device: 'Устройство',
};

function ChainChip({ role, label, caption, isFocus = false, title }) {
  const style = ROLE_STYLES[role] || ROLE_STYLES.device;
  const roleCaption = caption || ROLE_CAPTION[role] || null;

  return (
    <div
      title={title || label}
      className={[
        'inline-flex max-w-[14rem] flex-col rounded-lg border px-2 py-1 text-left shadow-sm',
        style,
        isFocus ? 'ring-2 ring-violet-400/70 ring-offset-1 ring-offset-white dark:ring-offset-slate-950' : '',
      ].join(' ')}
    >
      {roleCaption ? (
        <span className="text-[9px] font-medium uppercase tracking-wider opacity-60">{roleCaption}</span>
      ) : null}
      <span className="truncate text-xs font-medium leading-tight">{label}</span>
    </div>
  );
}

function Connector() {
  return (
    <span className="mx-0.5 hidden h-px w-3 shrink-0 bg-slate-300 sm:inline-block dark:bg-slate-600" aria-hidden="true" />
  );
}

function SpineLink() {
  return (
    <span className="flex h-8 items-center" aria-hidden="true">
      <Connector />
    </span>
  );
}

function childLabel(child) {
  if (child.input_number != null) {
    return `${child.serial_number || `#${child.id}`} · вх. ${child.input_number}`;
  }
  return child.serial_number || `#${child.id}`;
}

function AttachedDevices({ children }) {
  if (children.length === 0) {
    return <p className="ml-2 text-[11px] text-slate-500">Нет привязанных счётчиков или датчиков</p>;
  }

  const lastIndex = children.length - 1;

  return (
    <div className="flex items-center">
      <span className="h-px w-3 shrink-0 bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
      <ul className="flex flex-col">
        {children.map((child, index) => (
          <li key={child.id} className="flex items-center">
            <span className="relative w-3 shrink-0 self-stretch" aria-hidden="true">
              <span
                className={`absolute left-0 w-px bg-slate-300 dark:bg-slate-600 ${
                  children.length === 1
                    ? 'hidden'
                    : index === 0
                      ? 'top-1/2 h-1/2'
                      : index === lastIndex
                        ? 'top-0 h-1/2'
                        : 'inset-y-0'
                }`}
              />
              <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-slate-300 dark:bg-slate-600" />
            </span>
            <div className="py-0.5">
              <ChainChip
                role={child.role}
                caption={child.kind_label || ROLE_CAPTION[child.role]}
                label={childLabel(child)}
                isFocus={Boolean(child.is_focus)}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** УК → клиент → адрес → контроллер, счётчики и датчики от контроллера. */
export default function DeviceChainDiagram({ chain, isLoading = false, error = null }) {
  if (isLoading && !chain) {
    return (
      <div className="mt-4 animate-pulse rounded-xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="h-3 w-24 rounded bg-slate-200 dark:bg-slate-700" />
        <div className="mt-3 flex flex-wrap gap-2">
          <div className="h-9 w-28 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-9 w-36 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-9 w-32 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-9 w-24 rounded-lg bg-slate-200 dark:bg-slate-700" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="mt-4 rounded-xl border border-amber-300/40 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
        {error}
      </p>
    );
  }

  if (!chain?.spine?.length) {
    return null;
  }

  const children = chain.children || [];
  const controllerIndex = chain.spine.findIndex((node) => node.role === 'controller');
  const head = controllerIndex >= 0 ? chain.spine.slice(0, controllerIndex) : chain.spine;
  const controller = controllerIndex >= 0 ? chain.spine[controllerIndex] : null;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30">
      <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">Подключение</p>

      <div className="flex flex-wrap items-center gap-y-2">
        {head.map((node, index) => (
          <div key={`${node.role}-${node.id ?? node.label}`} className="flex items-start">
            {index > 0 ? <SpineLink /> : null}
            <ChainChip
              role={node.role}
              label={node.label}
              isFocus={Boolean(node.is_focus)}
              title={node.phone ? `${node.label} · ${node.phone}` : node.label}
            />
          </div>
        ))}

        {controller ? (
          <div className="flex items-center">
            {head.length > 0 ? <SpineLink /> : null}
            <ChainChip
              role={controller.role}
              label={controller.label}
              isFocus={Boolean(controller.is_focus)}
              title={controller.label}
            />
            <AttachedDevices children={children} />
          </div>
        ) : (
          <AttachedDevices children={children} />
        )}
      </div>
    </div>
  );
}
